import { get, remove } from 'lodash'

import Api from 'services/Api'
import { Format } from 'services/Dates'
import { getCSV, getTagNames, getAttributeNames, getProjectNames } from 'services/Utilities'
import { createErrorNotification } from 'store/actions/notifications'
import { store } from 'store/reducers'
import { currentLang, t } from 'translations'

export const exportProjectCSV = async ({ filter = {}, sortBy }) => {
  const {
    settings: { enableAssociatedProjects, enableAttributes }
  } = store.getState()
  const { projects, success } = await Api(`/projects/projects`, { params: { ...filter, sort: sortBy, lang: currentLang() } })

  if (!success) return store.dispatch(dispatch => dispatch(createErrorNotification()))

  let fields = [
    { value: 'name', label: t('manager.projects.csv.NAME') },
    { value: 'code', label: t('manager.projects.csv.CODE') },
    { value: 'status', label: t('manager.projects.csv.STATUS') },
    { value: 'defaultShiftTags', label: t('manager.projects.csv.DEFAULTSHIFTTAGS') },
    { value: 'tags', label: t('manager.projects.csv.TAGS') },
    { value: 'clients', label: t('manager.projects.csv.CLIENTS') },
    enableAssociatedProjects ? { value: 'associatedProjects', label: t('manager.projects.csv.ASSOCIATEDPROJECTS') } : null,
    { value: 'additionalInfo', label: t('manager.projects.csv.ADDITIONALINFO') },
    { value: 'notes', label: t('manager.projects.csv.NOTES') },
    enableAttributes ? { value: 'requiredAttributes', label: t('manager.projects.csv.REQUIREDATTRIBUTES') } : null,
    enableAttributes ? { value: 'preferredAttributes', label: t('manager.projects.csv.PREFERREDATTRIBUTES') } : null,
    { value: 'start', label: t('manager.projects.csv.START') },
    { value: 'end', label: t('manager.projects.csv.END') }
  ].filter(Boolean)

  const parsedProjects = projects.map(project => {
    return {
      ...project,
      status: t(`manager.projects.STATUSES.${project.status}`),
      tags: project.tags?.join(', ') || '',
      defaultShiftTags: getTagNames(project.defaultShiftTags),
      clients: getTagNames(project.clients),
      preferredAttributes: getAttributeNames(project.preferredAttributes),
      requiredAttributes: getAttributeNames(project.requiredAttributes),
      associatedProjects: getProjectNames(project.associatedProjects)
    }
  })

  getCSV(parsedProjects, fields, t('manager.projects.csv.CSV_NAME', { date: Format(new Date(), { outFormat: 'date' }) }))
}

export const sortBasedOnProjectStatus = (users, projectId) => {
  // takes a list of users and checks if they are locked to a project ID
  // returns an array with users locked to current project at the beginning
  // and those locked to other projects at the end

  let lockedToCurrent = []
  let preferredCurrent = []
  let lockedToOtherProj = []
  let preferredOtherProj = []
  for (const user of users) {
    if (get(user, 'projectRestrictions.locked', []).length > 0) {
      // checks if user is locked to the projectId passed & adds to array
      if (!user.projectRestrictions.locked.includes(projectId)) lockedToOtherProj.push(user)
      else lockedToCurrent.push(user)
    }

    if (get(user, 'projects.preferred', []).length > 0) {
      // checks if user is preferred to the projectId passed & adds to array
      if (!user.projectRestrictions.preferred.includes(projectId)) preferredOtherProj.push(user)
      else preferredCurrent.push(user)
    }
  }

  // removes all users preferred to current project from list, flags them, and adds them to the beginning of the list
  // Reverse is useful because otherwise the highest ranked person ends up at the end of the preferred users
  for (let user of preferredCurrent.reverse()) {
    remove(users, { _id: user._id })
    user.flags.otherFlags.preferredCurrent = true
    users.unshift(user)
  }

  // removes all users locked to current project from list, flags them, and adds them to the beginning of the list
  // Reverse is useful because otherwise the highest ranked person ends up at the end of the locked users
  for (let user of lockedToCurrent.reverse()) {
    remove(users, { _id: user._id })
    user.flags.otherFlags.lockedToCurrent = true
    users.unshift(user)
  }

  // removes all users locked to other projects, flags them, marks them inEligible, and adds them to the end of the list
  for (let user of lockedToOtherProj) {
    remove(users, { _id: user._id })
    user.flags.otherFlags.lockedToOtherProj = true
    user.isEligible = false
    users.push(user)
  }

  // removes all users preferred to other projects, flags them, marks them inEligible, and adds them to the end of the list
  for (let user of preferredOtherProj) {
    remove(users, { _id: user._id })
    user.flags.otherFlags.preferredOtherProj = true
    user.isEligible = false
    users.push(user)
  }

  return users
}
